import {
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { provideValueAccessor } from 's-ng-utils';
import { Wrapper } from 'bookraw/shared/components/form-input/wrapper';
import { isNumeric } from 'rxjs/internal-compatibility';
import { MatFormFieldAppearance } from '@angular/material/form-field/form-field';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'ext-select',
  styles: [`
    ::ng-deep .search-panel {
      transform: none !important;
      max-height: 350px;
    }
    .search-container {
      height: 45px;
      user-select: text;
    }
    .select-search-box {
      position: absolute;
      z-index: 100;
      top: 0;
      height: 45px;
      border-bottom: 1px solid #ccc;
      margin-bottom: 2px;
      border-radius: 4px 4px 0 0;
      overflow: hidden;
    }

    .select-search-box input {
      height: 100%;
      width: 100%;
      margin: 0;
      border: 0;
      padding: 0 0 0 10px;
      outline: none;
      font-size: 14px;
      box-sizing: border-box;
    }
    .search-no-result {
      margin-top: 45px;
      padding: 15px;
    }
    .option-img {
      vertical-align: middle;
      margin-right: 8px;
    }

    [dir='rtl'] .option-img {
      margin-right: 0;
      margin-left: 8px;
    }

    .mat-form-field {
      user-select: none;
    }
  `],
  template: `
    <mat-form-field
      [class.full-width]="fullWidth"
      [class.bookraw-style]="transparentStyle"
      [appearance]="appearance || 'standard'"
      [class.mat-form-field-invalid]="ngControl.invalid"
    >
      <mat-label>{{ label }}</mat-label>
      <mat-select
        #matSelectElement
        [required]="formControl.markRequired || required"
        [formControl]="formControl"
        [compareWith]="objectComparisonFunction"
        [multiple]="multiple"
        (selectionChange)="onSelectionChange($event)"
        (openedChange)="onOpenedChange($event)"
      >
        <div class="search-container" *ngIf="search">
          <div class="select-search-box mat-elevation-z3" [style.width]="searchBoxWidth">
            <input #searchInput type="text" placeholder="Search..." (keydown)="onSearchKeyDown($event)" (keyup)="onSearch($event)">
          </div>
          <div class="search-no-result" *ngIf="filteredItems && filteredItems.length === 0">No result.</div>
        </div>
        <mat-option *ngIf="clearable"> - </mat-option>
        <mat-option
          *ngFor="let item of filteredItems"
          [value]="convertItemValue(item)"
          [disabled]="isOptionDisabled(item)"
        >
          <img *ngIf="withImage" class="option-img" [src]="itemImageUrl(item)" height="25">
          {{ itemToString(item) }}
        </mat-option>
      </mat-select>
      <mat-error>{{ formControl?.getError('error') }}</mat-error>
    </mat-form-field>
  `,
  providers: [provideValueAccessor(ExtSelectComponent)],
})
export class ExtSelectComponent<TModel> extends Wrapper implements OnChanges {

  public searchBoxWidth: any = '100%';
  public filteredItems: TModel[] = [];
  private searchQuery: string = '';
  private filterChanged = new Subject<string>();
  @ViewChild('searchInput') searchInput: ElementRef;
  @ViewChild('matSelectElement') matSelectElement: MatSelect;
  @Output() selectionChange: EventEmitter<MatSelectChange> = new EventEmitter();
  @Output() searchTextChange: EventEmitter<string> = new EventEmitter();

  @Input() clearable: boolean;
  @Input() fullWidth?: boolean = true;
  @Input() appearance: MatFormFieldAppearance;
  @Input() label: string = '';
  @Input() select: string;
  @Input() items: TModel[];
  @Input() valuePropertyName?: string = null;
  @Input() isValueConvertableToNumber: boolean = false;
  @Input() textPropertyName?: string = 'name';
  @Input() multiple: boolean;
  @Input() withImage: boolean = false;
  @Input() imageUrl?: string = '';
  @Input() search: boolean = false;
  @Input() externalSearch: boolean = false;
  @Input() transparentStyle: boolean = false;
  @Input() convertItemValue: (T) => string = (item) => {
    const value = this.valuePropertyName ? item[this.valuePropertyName] : item;
    return this.isValueConvertableToNumber ? +value : value;
  }
  @Input() itemToString: (T) => string = (item) => {
    return this.textPropertyName && item.hasOwnProperty(this.textPropertyName) ? item[this.textPropertyName] : item.toString();
  }
  @Input() isOptionDisabled?: (T) => boolean = () => {
    return false;
  }

  @Input() itemImageUrl: (T) => string = (item) => {
    return item[this.imageUrl]?.['original'];
  }

  constructor(public injector: Injector) {
    super(injector);
    this.filterChanged.pipe(debounceTime(600)).subscribe(text => this.searchTextChange.emit(text));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.filteredItems = this.filter();
    }
  }

  public onOpenedChange(isOpen: boolean) {
    if (this.search && isOpen) {
      const panelElement: HTMLElement = this.matSelectElement.panel.nativeElement;
      panelElement.classList.add('search-panel');
      this.searchBoxWidth = panelElement.offsetWidth + 'px';
    } else {
      this.clearSearch();
    }
  }

  public onSearchKeyDown(event: KeyboardEvent) {
    event.stopImmediatePropagation();
  }

  public onSearch(event: KeyboardEvent) {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.filteredItems = this.filter();
    this.filterChanged.next(target.value);
  }


  public  objectComparisonFunction(option, value): boolean {
    const optionId = option && isNumeric(option.id) ? option.id : option;
    const valueId = value && isNumeric(value.id) ? value.id : value;
    return optionId === valueId;
  }

  public onSelectionChange(event: MatSelectChange) {
    this.selectionChange.emit(event);
  }

  private clearSearch() {
    if (this.search) {
      this.searchQuery = '';
      this.filteredItems = this.items;
      const input = this.searchInput.nativeElement;
      input.value = '';
      this.filterChanged.next('');
    }
  }

  private filter(): TModel[] {
    // Nem a meglévő listában filterezünk hanem request alapján ( keresést tehát maga a használó komponens végzi)
    if ( this.externalSearch) return this.items ?? [];
    // Belső keresés adott listában
    else {
      if (!this.items) {
        return [];
      }

      const query = this.searchQuery;

      // ha nincs keresés akkor az eredeti listát adjuk vissza
      if (!query || query.length === 0) {
        return this.items;
      }

      // beépített kereső, csak string alapon megy
      const filterValue = query.toString().toLowerCase();
      return this.items.filter(item => this.itemToString(item).toLowerCase().indexOf(filterValue) >= 0);
    }
  }
}
